// Mirrors the Go backend's marketing/application/dto.go and the Postgres
// enums in internal/migrations/000029_create_marketing_tables.up.sql
export type CampaignChannel = 'email' | 'sms'
export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'canceled'
export type CampaignTrigger = 'manual' | 'new_listing' | 'price_drop' | 'sequence_step'
export type RecipientStatus = 'pending' | 'sent' | 'failed' | 'skipped'
// Automation: template auto-use triggers (inventory alerts) and sequences.
export type TemplateTrigger = 'new_listing' | 'price_drop'
export type SequenceTrigger = 'customer_created' | 'deal_stage'
export type EnrollmentStatus = 'active' | 'completed' | 'stopped'

export const TEMPLATE_TRIGGER_LABELS: Record<TemplateTrigger, string> = {
  new_listing: 'New listing alert',
  price_drop: 'Price drop alert',
}

export const SEQUENCE_TRIGGER_LABELS: Record<SequenceTrigger, string> = {
  customer_created: 'When customer is created',
  deal_stage: 'When a deal enters a stage',
}

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  stopped: 'Stopped',
}

export const ENROLLMENT_STATUS_VARIANT: Record<
  EnrollmentStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  active: 'secondary',
  completed: 'default',
  stopped: 'outline',
}

export const CAMPAIGN_CHANNEL_LABELS: Record<CampaignChannel, string> = {
  email: 'Email',
  sms: 'SMS',
}

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  canceled: 'Canceled',
}

export const CAMPAIGN_STATUS_VARIANT: Record<
  CampaignStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'outline',
  scheduled: 'secondary',
  sending: 'secondary',
  sent: 'default',
  canceled: 'destructive',
}

export const CAMPAIGN_TRIGGER_LABELS: Record<CampaignTrigger, string> = {
  manual: 'Manual',
  new_listing: 'New listing',
  price_drop: 'Price drop',
  sequence_step: 'Nurture step',
}

export const RECIPIENT_STATUS_LABELS: Record<RecipientStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
}

export const RECIPIENT_STATUS_VARIANT: Record<
  RecipientStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'secondary',
  sent: 'default',
  failed: 'destructive',
  skipped: 'outline',
}

// Structured segment filter rules, stored as JSONB and evaluated live against
// the CRM tables. Every dimension optional; empty rules match everyone.
export interface SegmentRules {
  statuses?: string[] // customer status: lead/active/inactive
  deal_stages?: string[] // has a deal currently in one of these stages
  boat_owner?: boolean
  slip_renter?: boolean
  has_service_history?: boolean
  min_spend_cents?: number
  last_proposal_within_days?: number
  lead_sources?: string[] // acquisition source is one of these (LeadSource values)
  referred?: boolean // customer has a referrer on record
}

export interface Segment {
  id: string
  name: string
  description: string
  rules: SegmentRules
  created_at: string
  updated_at: string
}

export interface AudienceMember {
  customer_id: string
  first_name: string
  last_name: string
  company_name: string
  email: string
  phone: string
  email_opt_in: boolean
  sms_opt_in: boolean
}

export interface AudiencePreview {
  count: number
  skipped: number
  sample: AudienceMember[]
}

export interface CampaignTemplate {
  id: string
  name: string
  channel: CampaignChannel
  subject: string
  body: string
  trigger_type: TemplateTrigger | null
  created_at: string
  updated_at: string
}

export interface CampaignStats {
  recipients: number
  sent: number
  failed: number
  skipped: number
  opened: number
  clicked: number
}

export interface Campaign {
  id: string
  name: string
  template_id: string
  segment_id: string | null
  channel: CampaignChannel
  status: CampaignStatus
  trigger_type: CampaignTrigger
  source_boat_id: string | null
  subject: string
  body: string
  scheduled_at: string | null
  sent_at: string | null
  created_at: string
  updated_at: string
  stats: CampaignStats
}

export interface CampaignRecipient {
  id: string
  campaign_id: string
  customer_id: string
  customer_name: string
  customer_email: string
  address: string
  status: RecipientStatus
  error: string
  sent_at: string | null
  opened_at: string | null
  clicked_at: string | null
  created_at: string
}

// One row of the Audience tab: a customer with their channel consent.
// Missing consent rows read as the defaults (email in, SMS out).
export interface AudienceEntry {
  customer_id: string
  first_name: string
  last_name: string
  company_name: string
  email: string
  phone: string
  status: string
  email_opt_in: boolean
  sms_opt_in: boolean
  unsubscribed_at: string | null
}

// A customer's boat-buying interest; a listing matching any row triggers an
// inventory alert campaign.
export interface CustomerInterest {
  id: string
  customer_id: string
  customer_name: string
  make: string
  condition: string
  max_price_cents: number | null
  created_at: string
}

export interface InterestWriteBody {
  customer_id: string
  make?: string
  condition?: string
  max_price_cents?: number
}

// A drip sequence: enroll on customer creation or a deal stage, steps send on
// day offsets, enrollment stops when a deal reaches the stop stage.
export interface SequenceStep {
  id: string
  offset_days: number
  template_id: string
  channel: CampaignChannel
}

export interface Sequence {
  id: string
  name: string
  enroll_trigger: SequenceTrigger
  enroll_stage: string
  stop_stage: string
  steps: SequenceStep[]
  created_at: string
  updated_at: string
}

export interface SequenceWriteBody {
  name: string
  enroll_trigger: SequenceTrigger
  enroll_stage?: string
  stop_stage?: string
  steps: { offset_days: number; template_id: string; channel: CampaignChannel }[]
}

export interface SequenceEnrollment {
  id: string
  sequence_id: string
  sequence_name: string
  customer_id: string
  customer_name: string
  status: EnrollmentStatus
  current_step_index: number
  enrolled_at: string
  next_step_at: string | null
  stopped_reason: string
}

// Overview dashboard: campaign totals plus monthly send/engagement volume
// next to the finance ledger's marketing spend.
export interface MarketingTotals {
  campaigns: number
  recipients: number
  sent: number
  failed: number
  skipped: number
  opened: number
  clicked: number
}

export interface MonthlyMarketingPoint {
  month: string
  spend_cents: number
  sends: number
  opens: number
  clicks: number
}

export interface MarketingOverview {
  totals: MarketingTotals
  months: MonthlyMarketingPoint[]
}

// --- Lead acquisition (leads intake, sources, referrals) -----------------

// The lead-source enum lives in types/customers.ts (the customers module
// owns it, mirroring the backend); re-exported here for the Leads/Sources
// tabs which import everything from this module.
export { LEAD_SOURCE_LABELS } from '@/types/customers'
export type { LeadSource } from '@/types/customers'

// How a captured lead arrived: staff entry or an automated channel.
export type CaptureChannel = 'manual' | 'web_form' | 'facebook' | 'webhook'

export const CAPTURE_CHANNEL_LABELS: Record<CaptureChannel, string> = {
  manual: 'Manual',
  web_form: 'Web form',
  facebook: 'Facebook',
  webhook: 'Webhook',
}

export const CAPTURE_CHANNEL_VARIANT: Record<
  CaptureChannel,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  manual: 'outline',
  web_form: 'secondary',
  facebook: 'default',
  webhook: 'secondary',
}

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'lost'

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  lost: 'Lost',
}

export const LEAD_STATUS_VARIANT: Record<
  LeadStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  new: 'default',
  contacted: 'secondary',
  qualified: 'secondary',
  converted: 'default',
  lost: 'destructive',
}

export interface Lead {
  id: string
  first_name: string
  last_name: string
  company_name?: string
  email: string
  phone: string
  source: string // LeadSource; '' = unknown
  channel: CaptureChannel
  status: LeadStatus
  notes: string
  assigned_to: string
  referrer_id: string | null
  converted_customer_id: string | null
  converted_at: string | null
  created_at: string
  updated_at: string
}

export interface LeadWriteBody {
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  phone?: string
  source?: string
  status?: string
  notes?: string
  assigned_to?: string
  referrer_id?: string
}

export type ReferralStatus = 'pending' | 'contacted' | 'qualified' | 'converted' | 'lost'

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  pending: 'Pending',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  lost: 'Lost',
}

export const REFERRAL_STATUS_VARIANT: Record<
  ReferralStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'outline',
  contacted: 'secondary',
  qualified: 'secondary',
  converted: 'default',
  lost: 'destructive',
}

export interface Referral {
  id: string
  referrer_id: string
  referred_customer_id: string | null
  referred_lead_id: string | null
  referred_name: string
  status: ReferralStatus
  notes: string
  referral_date: string
  created_at: string
  updated_at: string
}

export interface ReferralWriteBody {
  referrer_id: string
  referred_customer_id?: string
  referred_lead_id?: string
  referred_name: string
  status?: string
  notes?: string
  referral_date?: string
}

// Lead-source attribution: the acquisition funnel per source.
export interface SourceAttribution {
  source: string // LeadSource; '' = unknown
  leads: number
  leads_converted: number
  conversion_rate: number
  customers: number
  deals: number
  won: number
  won_value_cents: number
}

export interface MonthlyLeadPoint {
  month: string
  source: string
  leads: number
}

export interface ReferralSummary {
  total: number
  converted: number
  won_value_cents: number
}

export interface Attribution {
  sources: SourceAttribution[]
  months: MonthlyLeadPoint[]
  referrals: ReferralSummary
}
